import { User, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn, getFlagEmoji } from '@/lib/utils';

export interface RunnerDisplayData {
    utmbId?: string | null;
    name?: string | null;
    pictureUri?: string | null;
    gender?: 'MALE' | 'FEMALE' | string | null;
    utmbIndex?: number | null;
    ageGroup?: string | null;
    nationality?: string | null;
    potentialScore?: number | null;
    result?: number | null;
}

interface RunnerCardProps {
    runner: RunnerDisplayData;
    action?: React.ReactNode;
    isHighlight?: boolean;
    showPotentialScore?: boolean;
    className?: string;
    onClick?: () => void;
}

export function RunnerCard({ runner, action, isHighlight, showPotentialScore = false, className, onClick }: RunnerCardProps) {
    const { t } = useTranslation();

    return (
        <div
            className={cn(
                "flex items-center gap-4 p-3 rounded-2xl border transition-all duration-200 min-w-0",
                isHighlight
                    ? "bg-emerald-50/80 border-emerald-200 shadow-sm"
                    : "bg-white border-slate-100 shadow-sm hover:border-slate-200",
                onClick && "cursor-pointer",
                className
            )}
            onClick={onClick}
            onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            {/* Avatar */}
            <div className="relative flex-shrink-0">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 border border-slate-100">
                    {runner.pictureUri ? (
                        <img src={runner.pictureUri} className="w-full h-full object-cover" alt={runner.name || ""} />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <User className="text-slate-300" size={24} />
                        </div>
                    )}
                </div>
                {runner.gender && (
                    <span
                        className={cn(
                            "absolute -bottom-1 -right-1 w-4 h-4 border-2 border-white rounded-full",
                            runner.gender === 'MALE' ? "bg-blue-500" : "bg-pink-500"
                        )}
                    />
                )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <p className={cn("font-bold truncate leading-tight", isHighlight ? "text-emerald-900" : "text-slate-900")}>
                    {runner.name}
                </p>

                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                        <span className="text-slate-400 font-normal">UTMB</span>
                        <span className="text-slate-700">{Math.round(runner.utmbIndex || 0)}</span>
                    </div>

                    <span className="text-lg leading-none" title={runner.nationality || t('unknown')}>
                        {getFlagEmoji(runner.nationality)}
                    </span>

                    {showPotentialScore && runner.potentialScore != null && (
                        <div className="flex items-center gap-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md">
                            <Zap size={10} className="text-indigo-400" />
                            <span>+{Math.round(runner.potentialScore)}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Action slot */}
            {action && (
                <div className="flex-shrink-0">
                    {action}
                </div>
            )}
        </div>
    );
}
